import { AnimatedPageHeader } from '@components/index'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EVENTS_TABLE_PAGE_SIZE,
  TOP_APARTMENTS_FETCH_LIMIT,
  type AnalyticsEventType
} from '@services/event-analytics/constants'
import {
  rangeFromPreset,
  previousRangeFromPreset,
  type EventAnalyticsPreset
} from '@services/event-analytics/dateRange'
import {
  useEventAnalyticsBreakdown,
  useEventAnalyticsEvents,
  useEventAnalyticsSummary,
  useEventAnalyticsTimeseries
} from '@services/event-analytics/queries/useEventAnalytics'
import type {
  AnalyticsBreakdownDimension,
  AnalyticsGranularity
} from '@services/event-analytics/types'

import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

import { EventAnalyticsBreakdownChart } from '../components/EventAnalyticsBreakdownChart'
import {
  EventAnalyticsKpiCards,
  formatEventAnalyticsPeriod
} from '../components/EventAnalyticsKpiCards'
import { EventAnalyticsEventsTable } from '../components/EventAnalyticsEventsTable'
import { EventAnalyticsTimeseriesChart } from '../components/EventAnalyticsTimeseriesChart'
import { EventAnalyticsTopApartments } from '../components/EventAnalyticsTopApartments'

const PRESETS: EventAnalyticsPreset[] = ['7d', '30d', '90d']

export function EventAnalyticsDashboard() {
  const { t } = useTranslation('eventAnalytics')
  const [preset, setPreset] = useState<EventAnalyticsPreset>('30d')
  const [breakdownDimensionA, setBreakdownDimensionA] =
    useState<AnalyticsBreakdownDimension>('listing_type')
  const [breakdownDimensionB, setBreakdownDimensionB] =
    useState<AnalyticsBreakdownDimension>('is_authenticated')
  const [timeseriesGranularity, setTimeseriesGranularity] =
    useState<AnalyticsGranularity>('day')
  const [timeseriesEventFilter, setTimeseriesEventFilter] =
    useState<AnalyticsEventType | null>(null)
  const [tableEventFilter, setTableEventFilter] =
    useState<AnalyticsEventType | null>(null)
  const [tablePage, setTablePage] = useState(0)

  const range = useMemo(() => rangeFromPreset(preset), [preset])
  const previousRange = useMemo(() => previousRangeFromPreset(preset), [preset])
  const periodLabel = formatEventAnalyticsPeriod(range.from, range.to)
  const previousPeriodLabel = formatEventAnalyticsPeriod(
    previousRange.from,
    previousRange.to
  )

  useEffect(() => {
    setTablePage(0)
    // Hour granularity on 90 days produces too many points — reset to day
    if (preset === '90d' && timeseriesGranularity === 'hour') {
      setTimeseriesGranularity('day')
    }
  }, [preset, tableEventFilter, timeseriesGranularity])

  const {
    data: summary,
    isLoading: summaryLoading,
    isFetching: summaryFetching,
    isError: summaryError
  } = useEventAnalyticsSummary(range)

  const {
    data: previousSummary,
    isLoading: previousSummaryLoading,
    isFetching: previousSummaryFetching
  } = useEventAnalyticsSummary(previousRange)

  const { data: timeseries, isLoading: timeseriesLoading, isFetching: timeseriesFetching } =
    useEventAnalyticsTimeseries({
      ...range,
      granularity: timeseriesGranularity,
      ...(timeseriesEventFilter ? { event: timeseriesEventFilter } : {})
    })

  const { data: breakdownA, isLoading: breakdownALoading, isFetching: breakdownAFetching } =
    useEventAnalyticsBreakdown({
      ...range,
      dimension: breakdownDimensionA
    })

  const { data: breakdownB, isLoading: breakdownBLoading, isFetching: breakdownBFetching } =
    useEventAnalyticsBreakdown({
      ...range,
      dimension: breakdownDimensionB
    })

  const { data: events, isLoading: eventsLoading, isFetching: eventsFetching } =
    useEventAnalyticsEvents({
      ...range,
      limit: EVENTS_TABLE_PAGE_SIZE,
      offset: tablePage * EVENTS_TABLE_PAGE_SIZE,
      ...(tableEventFilter ? { event: tableEventFilter } : {})
    })

  const { data: clickEvents, isLoading: clickEventsLoading, isFetching: clickEventsFetching } =
    useEventAnalyticsEvents({
      ...range,
      limit: TOP_APARTMENTS_FETCH_LIMIT,
      offset: 0,
      event: 'apartment_clicked'
    })

  const hasError = summaryError

  return (
    <div className="space-y-6">
      <AnimatedPageHeader
        title={t('title')}
        description={
          <>
            {t('description')}
            {periodLabel ? (
              <span className="mt-1 block text-sm text-muted-foreground">
                {periodLabel}
                {previousPeriodLabel ? (
                  <span className="block text-xs">
                    {t('trends.comparisonPeriod', { period: previousPeriodLabel })}
                  </span>
                ) : null}
              </span>
            ) : null}
          </>
        }
      />

      <div className="flex flex-wrap gap-2">
        {PRESETS.map((value) => (
          <Button
            key={value}
            type="button"
            size="sm"
            variant={preset === value ? 'default' : 'outline'}
            className={cn(preset !== value && 'bg-background')}
            onClick={() => setPreset(value)}
          >
            {t(`presets.${value}`)}
          </Button>
        ))}
      </div>

      {hasError ? (
        <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {t('error')}
        </div>
      ) : null}

      <EventAnalyticsKpiCards
        summary={summary}
        previousSummary={previousSummary}
        loading={summaryLoading || previousSummaryLoading || summaryFetching || previousSummaryFetching}
      />

      <EventAnalyticsTimeseriesChart
        data={timeseries}
        loading={timeseriesLoading || timeseriesFetching}
        granularity={timeseriesGranularity}
        onGranularityChange={setTimeseriesGranularity}
        eventFilter={timeseriesEventFilter}
        onEventFilterChange={setTimeseriesEventFilter}
        disableHourGranularity={preset === '90d'}
      />

      <div className="grid gap-6 xl:grid-cols-2">
        <EventAnalyticsBreakdownChart
          data={breakdownA}
          loading={breakdownALoading || breakdownAFetching}
          dimension={breakdownDimensionA}
          onDimensionChange={setBreakdownDimensionA}
        />
        <EventAnalyticsBreakdownChart
          data={breakdownB}
          loading={breakdownBLoading || breakdownBFetching}
          dimension={breakdownDimensionB}
          onDimensionChange={setBreakdownDimensionB}
        />
      </div>

      <EventAnalyticsTopApartments
        items={clickEvents?.items}
        loading={clickEventsLoading || clickEventsFetching}
      />

      <EventAnalyticsEventsTable
        data={events}
        loading={eventsLoading || eventsFetching}
        page={tablePage}
        pageSize={EVENTS_TABLE_PAGE_SIZE}
        onPageChange={setTablePage}
        eventFilter={tableEventFilter}
        onEventFilterChange={setTableEventFilter}
      />
    </div>
  )
}
